"use client";

import { createPortal } from "react-dom";
import { useState, useRef, useEffect, useMemo } from "react";

export interface ChildRuleRow {
    min_age: number | null;
    max_age: number | null;
    fee: number;
}

export interface PriceDetailRow {
    paxRange: string;
    perPersonDouble: number | null;
    singleRoom: number | null;
    extraBed: number | null;
    childrenFee: number | null;
    childRules?: ChildRuleRow[];
}

export interface PriceDetailPeriod {
    packageName: string;
    periodStart: string;
    periodEnd: string;
    rows: PriceDetailRow[];
}

interface PriceDetailTableProps {
    periods: PriceDetailPeriod[];
    currency?: string;
    variant?: 'group' | 'private' | 'daily';
    t?: (key: string, fallback?: string) => string;
}

const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return dateString;
        }
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        return `${day.toString().padStart(2, '0')} ${month}`;
    } catch {
        return dateString;
    }
};

const formatPrice = (price: number | null, currency: string = 'EUR'): string => {
    if (price === null || price === undefined) return '-';
    return `€ ${price.toFixed(2)}`;
};

const formatPackageLabel = (packageName: string): string => {
    let name = (packageName ?? '').trim();
    name = name.replace(/\s+Package\s*$/i, '').trim();
    name = name.replace(/\bDefault\b/gi, '').trim();
    if (!name) return 'Standard';
    return name.replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Yaş kuralı veya düz children fee ile gösterilecek anlamlı içerik var mı */
function rowHasChildFeeContent(row: PriceDetailRow): boolean {
    const rules = row.childRules;
    if (Array.isArray(rules) && rules.length > 0) {
        return rules.some((rule) => {
            const fee = Number(rule.fee);
            return Number.isFinite(fee) && fee > 0;
        });
    }
    if (row.childrenFee == null) return false;
    const fee = Number(row.childrenFee);
    return Number.isFinite(fee) && fee > 0;
}

function childFeeDisplayRules(row: PriceDetailRow): ChildRuleRow[] {
    if (Array.isArray(row.childRules) && row.childRules.length > 0) {
        return row.childRules.filter((rule) => {
            const fee = Number(rule.fee);
            return Number.isFinite(fee) && fee > 0;
        });
    }
    const fee = Number(row.childrenFee ?? 0);
    if (Number.isFinite(fee) && fee > 0) {
        return [{ min_age: null, max_age: null, fee }];
    }
    return [];
}

export default function PriceDetailTable({ periods, currency = 'EUR', variant = 'private', t }: PriceDetailTableProps) {
    const [openChildDropdown, setOpenChildDropdown] = useState<string | null>(null);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const anchorButtonRef = useRef<HTMLElement | null>(null);

    const isGroup = variant === 'group';
    const isDaily = variant === 'daily';

    const label = (key: string, fallback: string) => (t ? t(key, fallback) : fallback);

    const openChildKey = openChildDropdown;
    const [openPeriodIdx, openRowIdx] = useMemo(() => {
        if (!openChildKey) return [null, null];
        const parts = openChildKey.split("-").map(Number);
        return [parts[0], parts[1]];
    }, [openChildKey]);

    const openRow = useMemo(() => {
        if (openPeriodIdx == null || openRowIdx == null || !periods[openPeriodIdx]) return null;
        return periods[openPeriodIdx].rows[openRowIdx] ?? null;
    }, [periods, openPeriodIdx, openRowIdx]);

    const closeChildDropdown = () => {
        setOpenChildDropdown(null);
        setAnchorRect(null);
        anchorButtonRef.current = null;
    };

    const openChildDropdownAt = (periodIndex: number, rowIndex: number, button: HTMLElement) => {
        setAnchorRect(button.getBoundingClientRect());
        anchorButtonRef.current = button;
        setOpenChildDropdown(`${periodIndex}-${rowIndex}`);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (dropdownRef.current?.contains(target)) return;
            if (anchorButtonRef.current?.contains(target)) return;
            closeChildDropdown();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!openChildDropdown) return;
        const handleScroll = () => closeChildDropdown();
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [openChildDropdown]);

    if (!periods || periods.length === 0) {
        return null;
    }

    const portalDropdown =
        typeof document !== "undefined" &&
        openChildDropdown &&
        anchorRect &&
        openRow ? (
            createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999]"
                    style={{
                        top: anchorRect.bottom + 4,
                        right: document.documentElement.clientWidth - anchorRect.right,
                    }}
                >
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[180px]">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-3 py-2 text-left font-semibold text-alsero-dark-gray whitespace-nowrap">{label('tour_detail.children_fee_age', 'Ages')}</th>
                                    <th className="px-3 py-2 text-left font-semibold text-alsero-dark-gray whitespace-nowrap">{label('tour_detail.children_fee_price', 'Price')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {childFeeDisplayRules(openRow).map((rule, i) => (
                                    <tr key={i} className="border-b border-gray-100 last:border-0">
                                        <td className="px-3 py-2 text-alsero-dark-gray">{rule.min_age != null ? rule.min_age : '-'} - {rule.max_age != null ? rule.max_age : '-'}</td>
                                        <td className="px-3 py-2 text-alsero-dark-gray">{formatPrice(rule.fee, currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>,
                document.body
            )
        ) : null;

    return (
        <>
            {portalDropdown}
            <div className="space-y-4">
                {periods.map((period, periodIndex) => (
                    <details
                        key={`${period.packageName}-${period.periodStart}-${period.periodEnd}-${periodIndex}`}
                        open
                        className="group w-full border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                        <summary className="relative list-none cursor-pointer bg-alsero-dark-gray px-4 py-4 pr-12 text-left transition-colors hover:bg-alsero-dark-gray/90 sm:px-6 sm:pr-14 [&::-webkit-details-marker]:hidden">
                            <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
                                {!isGroup ? (
                                    <>
                                        <div className="min-w-0 flex-1 text-base font-bold leading-snug text-white break-words md:text-lg">
                                            <span>{formatPackageLabel(period.packageName)}</span>{' '}
                                            <span className="font-semibold opacity-95">{label('reservation.package', 'Package')}</span>
                                        </div>
                                        <div className="min-w-0 text-sm font-medium leading-snug text-white break-words md:shrink-0 md:text-right">
                                            {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                                        </div>
                                    </>
                                ) : (
                                    <div className="min-w-0 flex-1 text-sm font-medium leading-snug text-white break-words">
                                        {formatDate(period.periodStart)} - {formatDate(period.periodEnd)}
                                    </div>
                                )}
                            </div>
                            <span
                                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white transition-transform duration-200 group-open:rotate-180 sm:right-4"
                                aria-hidden
                            >
                                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </span>
                        </summary>

                        <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain border-t border-gray-200 touch-pan-x [-webkit-overflow-scrolling:touch]">
                            <table className={`w-full border-collapse bg-white ${isDaily ? 'min-w-[280px] sm:min-w-[360px]' : 'min-w-[480px] sm:min-w-[560px] md:min-w-[600px]'}`}>
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        {!isGroup && (
                                            <th className="border-r border-gray-200 px-2 py-2.5 text-left text-xs font-semibold text-alsero-dark-gray sm:px-4 sm:py-4 sm:text-sm">
                                                {label('tour_detail.price_people', 'People')}
                                            </th>
                                        )}
                                        <th className={`${isDaily ? '' : 'border-r border-gray-200'} px-2 py-2.5 text-left text-xs font-semibold leading-tight text-alsero-dark-gray sm:px-4 sm:py-4 sm:text-sm`}>
                                            {isDaily ? (
                                                <>
                                                    <span className="md:hidden">{label('tour_detail.daily_price_per_person_short', 'Per person')}</span>
                                                    <span className="hidden md:inline whitespace-nowrap">{label('tour_detail.daily_price_per_person', 'Price per person')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="md:hidden">{label('tour_detail.price_per_person_double_short', 'Per. / double')}</span>
                                                    <span className="hidden md:inline whitespace-nowrap">{label('tour_detail.price_per_person_double', 'Per. Person in Double')}</span>
                                                </>
                                            )}
                                        </th>
                                        {!isDaily && (
                                            <>
                                                <th className="border-r border-gray-200 px-2 py-2.5 text-left text-xs font-semibold leading-tight text-alsero-dark-gray sm:px-4 sm:py-4 sm:text-sm">
                                                    <span className="md:hidden">{label('tour_detail.price_single_room_short', 'Single')}</span>
                                                    <span className="hidden md:inline whitespace-nowrap">{label('tour_detail.price_single_room', 'Single Room')}</span>
                                                </th>
                                                <th className="border-r border-gray-200 px-2 py-2.5 text-left text-xs font-semibold leading-tight text-alsero-dark-gray sm:px-4 sm:py-4 sm:text-sm">
                                                    <span className="md:hidden">{label('tour_detail.price_extra_bed_short', 'Extra')}</span>
                                                    <span className="hidden md:inline whitespace-nowrap">{label('tour_detail.price_extra_bed', 'Extra Bed')}</span>
                                                </th>
                                            </>
                                        )}
                                        <th className="px-2 py-2.5 text-left text-xs font-semibold leading-tight text-alsero-dark-gray sm:px-4 sm:py-4 sm:text-sm">
                                            <span className="md:hidden">{label('tour_detail.price_children_fee_short', 'Child.')}</span>
                                            <span className="hidden md:inline whitespace-nowrap">{label('tour_detail.price_children_fee', 'Children Fee')}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {period.rows.map((row, rowIndex) => (
                                        <tr
                                            key={rowIndex}
                                            className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}
                                        >
                                            {!isGroup && (
                                                <td className="border-r border-gray-200 px-2 py-2.5 text-xs font-medium text-alsero-light-gray sm:px-4 sm:py-3 sm:text-sm">
                                                    {row.paxRange || '-'}
                                                </td>
                                            )}
                                            <td className={`${isDaily ? '' : 'border-r border-gray-200'} px-2 py-2.5 text-xs text-alsero-dark-gray tabular-nums sm:px-4 sm:py-3 sm:text-sm`}>
                                                {formatPrice(row.perPersonDouble, currency)}
                                            </td>
                                            {!isDaily && (
                                                <>
                                                    <td className="border-r border-gray-200 px-2 py-2.5 text-xs text-alsero-dark-gray tabular-nums sm:px-4 sm:py-3 sm:text-sm">
                                                        {formatPrice(row.singleRoom, currency)}
                                                    </td>
                                                    <td className="border-r border-gray-200 px-2 py-2.5 text-xs text-alsero-dark-gray tabular-nums sm:px-4 sm:py-3 sm:text-sm">
                                                        {formatPrice(row.extraBed, currency)}
                                                    </td>
                                                </>
                                            )}
                                            <td className="px-2 py-2.5 text-xs text-alsero-dark-gray sm:px-4 sm:py-3 sm:text-sm">
                                                {rowHasChildFeeContent(row) ? (
                                                    <button
                                                        type="button"
                                                        onMouseEnter={(e) => openChildDropdownAt(periodIndex, rowIndex, e.currentTarget)}
                                                        onClick={(e) => {
                                                            if (openChildDropdown === `${periodIndex}-${rowIndex}`) {
                                                                closeChildDropdown();
                                                            } else {
                                                                openChildDropdownAt(periodIndex, rowIndex, e.currentTarget);
                                                            }
                                                        }}
                                                        className="inline-flex items-center justify-center py-1.5 px-3 rounded-md bg-alsero-first text-white text-sm font-medium hover:bg-alsero-first/90 focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-1 transition-colors shadow-sm"
                                                    >
                                                        {label('tour_detail.children_fee_view', 'View')}
                                                    </button>
                                                ) : (
                                                    <span className="text-alsero-light-gray">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </details>
                ))}
            </div>
        </>
    );
}
